import { useQuery } from "@apollo/client";
import {
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Redirect } from "react-router-dom";
import {
  GetOrganizationSettingsQuery,
  GetOrganizationSettingsQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Integrations from "@apps/org-settings/components/integrations";
import Notifications from "@apps/org-settings/components/notifications";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Container from "@components/container/container";
import GraphqlError from "@components/error/graphql-error";
import Loading from "@components/loading/loading";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { role } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import Ai from "./components/ai";
import Billing from "./components/billing";
import CareerTrackForm from "./components/competencies/career-track-form/career-track-form";
import CompetencyFormPage from "./components/competencies/competency-form-page";
import RolesAndCompetencies from "./components/competencies/roles-and-competencies";
import CoreValues from "./components/core-values";
import DirectorySync from "./components/directory-sync";
import Members from "./components/members";
import Settings from "./components/settings";
import SlackConfiguration from "./components/slack-configuration";
import TalentIndicators from "./components/talent-indicators";
import Teams from "./components/teams";
import getOrganizationSettingsQuery from "./graphql/get-organization-settings-query";

const IT_ADMIN_ALLOWED_TABS = new Set([
  "directory-sync",
  "integrations",
  "notifications",
]);

const FULL_WIDTH_PATTERNS = ["/members", "/competencies", "/career-track"];

const isFullWidthPage = (pathname: string) =>
  FULL_WIDTH_PATTERNS.some((pattern) => pathname.includes(pattern));

const OrgSettings = () => {
  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  useDocumentTitle(`${label("organization", { capitalize: true })} settings`);
  const location = useLocation<TFLocationState>();
  const { path } = useRouteMatch();
  const { organizationId: organizationIdParam } = useParams<{
    organizationId: string;
  }>();
  const organizationId =
    parseInt(organizationIdParam) || currentOrganization.id;

  // HOOKS
  const { data, loading } = useQuery<
    GetOrganizationSettingsQuery,
    GetOrganizationSettingsQueryVariables
  >(getOrganizationSettingsQuery);
  const organizations = data?.me?.organizations
    ? assertEdgesNonNull(data.me.organizations)
    : [];
  const selectedOrganization = organizations.find(
    ({ id }) => id === organizationId,
  );
  const membershipRole = selectedOrganization?.userMembership?.role ?? null;
  const hasSettingsAccess =
    membershipRole !== null &&
    [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(membershipRole);

  if (selectedOrganization?.userMembership && !hasSettingsAccess) {
    return <Redirect to="/" />;
  }

  const firstAllowedTab =
    membershipRole === role.itAdmin ? "directory-sync" : "settings";
  const defaultTabPath = selectedOrganization
    ? `/settings/organization/${selectedOrganization.id}/${firstAllowedTab}`
    : "/";
  const hasAccess = (tabValue: string) => {
    if (!membershipRole) {
      return false;
    }
    if (membershipRole === role.itAdmin) {
      return IT_ADMIN_ALLOWED_TABS.has(tabValue);
    }
    return [role.globalAdmin, role.peopleAdmin].includes(membershipRole);
  };

  return (
    <Container
      ariaLabel="Organization Settings"
      maxWidth={isFullWidthPage(location.pathname) ? "full" : "lg"}
      padding="settings"
    >
      {loading ? (
        <Loading />
      ) : selectedOrganization ? (
        <div className="text-gray-800">
          <Switch>
            <Route exact path={[`${path}/settings`, `${path}`]}>
              {hasAccess("settings") ? (
                <Settings organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/members`}>
              {hasAccess("members") ? (
                <Members organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/teams`}>
              {hasAccess("teams") ? (
                <Teams organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/core-values`}>
              {hasAccess("core-values") ? (
                <CoreValues organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/ai`}>
              {hasAccess("ai") ? (
                <Ai organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/workspace-skills`}>
              <Redirect
                to={`/settings/organization/${selectedOrganization.id}/ai`}
              />
            </Route>
            <Route exact path={`${path}/billing`}>
              {hasAccess("billing") ? (
                <Billing organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/directory-sync`}>
              {hasAccess("directory-sync") ? (
                <DirectorySync organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/integrations`}>
              {hasAccess("integrations") ? (
                <Integrations organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/slack`}>
              {waffle.flag_is_active("new-slack-settings") &&
              hasAccess("integrations") ? (
                <SlackConfiguration organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/notifications`}>
              {hasAccess("notifications") ? (
                <Notifications organization={selectedOrganization} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            {waffle.flag_is_active("talent-indicators") && (
              <Route exact path={`${path}/talent-indicators`}>
                {hasAccess("talent-indicators") ? (
                  <TalentIndicators organization={selectedOrganization} />
                ) : (
                  <Redirect to={defaultTabPath} />
                )}
              </Route>
            )}
            <Route exact path={`${path}/competencies`}>
              {hasAccess("competencies") ? (
                <RolesAndCompetencies
                  organizationId={selectedOrganization.id}
                />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/competencies/new`}>
              {hasAccess("competencies") ? (
                <CompetencyFormPage organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/competencies/edit/:competencyId`}>
              {hasAccess("competencies") ? (
                <CompetencyFormPage organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/career-track/new`}>
              {hasAccess("competencies") ? (
                <CareerTrackForm organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
            <Route exact path={`${path}/career-track/edit/:careerTrackId`}>
              {hasAccess("competencies") ? (
                <CareerTrackForm organizationId={selectedOrganization.id} />
              ) : (
                <Redirect to={defaultTabPath} />
              )}
            </Route>
          </Switch>
        </div>
      ) : (
        <GraphqlError
          whatDidNotWork="Organization settings could not be loaded."
          error={new Error("Organization settings could not be loaded")}
        />
      )}
    </Container>
  );
};

export default OrgSettings;
